import { useEffect, useState } from 'react';

import { fg } from '@atlaskit/platform-feature-flags';

import { fetchAggResponse, logger } from '../../../../common';
import { createAtlAttributionHeader } from '../../../../common/fetch/atl-attribution-header';

import { IssueSearchQuery } from './gql';
import type { IssueEdge, IssueSearchQueryResponse, IssueSearchQueryVariables } from './types';

const MAIN_ISSUE_SEARCH_QUERY_OPERATION_NAME = 'IssueSearchStable';

const fetchIssueSearchQuery = async ({
	variables,
	workspaceId,
	product,
}: {
	variables: IssueSearchQueryVariables;
	workspaceId: string;
	product: string;
}): Promise<IssueSearchQueryResponse | null | undefined> => {
	return fetchAggResponse<IssueSearchQueryVariables, IssueSearchQueryResponse>({
		variables,
		graphQuery: IssueSearchQuery,
		operationName: MAIN_ISSUE_SEARCH_QUERY_OPERATION_NAME,
		headers: {
			'X-ExperimentalApi': 'JiraIssueSearch',
			...createAtlAttributionHeader({
				atlWorkspaceId: workspaceId,
				tenantId: variables.cloudId,
				product,
			}),
		},
	})
		.then((response) => response.data)
		.catch((error) => {
			logger.error({ error, functionName: 'useIssueSearchQuery' });
			// rethrow error to be handled by the caller
			throw error;
		});
};

export const useIssueSearchQuery = ({
	cloudId,
	jql,
	workspaceId,
	product,
}: {
	cloudId: string;
	jql?: string;
	workspaceId: string;
	product: string;
}) => {
	const [loading, setIsLoading] = useState(false);
	const [error, setError] = useState(false);
	const [issues, setIssues] = useState<IssueEdge[] | undefined>();
	const [totalIssuesCount, setTotalIssuesCount] = useState<number | undefined>();

	useEffect(() => {
		const fetchData = async () => {
			try {
				setIsLoading(true);

				const data = await fetchIssueSearchQuery({
					variables: {
						cloudId,
						first: 5,
						issueSearchInput: { jql: jql || '' },
					},
					workspaceId,
					product,
				});

				if (data?.errors?.length && fg('kd_jqlcard_additional_error_handling')) {
					setError(true);
					throw new Error(data.errors[0].message || 'Unknown error');
				}

				setIssues(data?.jira?.issueSearchStable?.edges || []);
				setTotalIssuesCount(data?.jira?.issueSearchStable?.totalIssueSearchResultCount);
			} catch (e) {
				logger.error({ error: e, functionName: 'useIssueNavigatorQuery' });
				setError(true);
			} finally {
				setIsLoading(false);
			}
		};

		if (Boolean(jql)) {
			fetchData();
		}
	}, [cloudId, jql, workspaceId, product]);

	return {
		issues,
		totalIssuesCount,
		loading,
		error,
	};
};
